import { Alert, AlertTitle, Grid, Paper } from '@mui/material';
import { t } from 'i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useEffect, useMemo, useState } from 'react';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import SearchField from 'src/shared/components/form/searchField';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import PaymentAccountingDataGrid from '../components/paymentAccountingDataGrid';
import PaymentPageTabNavigation from '../components/paymentTabNavigation';
import { PaymentAccountingSearchFilter } from '../models/paymentAccountingSearchFilter';

export const PaymentAccountingPage = () => {
  const isMounted = useIsMounted();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [paymentAccountingSearchFilter, setPaymentAccountingSearchFilter] = useLocalStorage(
    LocalStorageKeys.PaymentAccountingSearchFilter,
    {} as unknown as PaymentAccountingSearchFilter,
  );

  const searchFilter: PaymentAccountingSearchFilter = useMemo(() => {
    return {
      searchString: paymentAccountingSearchFilter?.searchString,
      ownerId: paymentAccountingSearchFilter?.ownerId,
    } as unknown as PaymentAccountingSearchFilter;
  }, [paymentAccountingSearchFilter?.ownerId, paymentAccountingSearchFilter?.searchString]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setPaymentAccountingSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setPaymentAccountingSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setPaymentAccountingSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setPaymentAccountingSearchFilter, tempSearchString]);

  return (
    <GridWrapper>
      <Grid item xs={12} md={4}>
        <MainContentHeader title={t('invoice:payment.paymentAccountingPageTitle')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <PaymentPageTabNavigation />
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.searchString || ''}
                disabled={!searchFilter.ownerId || searchFilter.ownerId === 0}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            rightContent={
              <InvoiceGroups
                callback={(ownId) => {
                  setPaymentAccountingSearchFilter((prev) => ({ ...prev, ...{ ownerId: ownId } }));
                }}
                autoSelect
                ownerId={searchFilter.ownerId}
              />
            }
          />
          {!!searchFilter.ownerId && searchFilter.ownerId > 0 && <PaymentAccountingDataGrid searchFilter={searchFilter} />}
          {(!searchFilter.ownerId || searchFilter.ownerId === 0) && (
            <Alert severity='info'>
              <AlertTitle>{t('invoice:transactionPage.selectOwnerIdWarningTitle')}</AlertTitle>
              {t('invoice:transactionPage.selectOwnerIdWarningText')}
            </Alert>
          )}
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
